/**
 * Global header displayed at the top of most pages.
 *
 * Uses a route → title/subtitle lookup table.
 * Hidden on pages that render their own headers (entity pages, settings, etc.).
 */

import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { useLocation } from 'react-router-dom';
import { useAsync } from 'react-use';
import { Header as BackstageHeader, HeaderLabel } from '@backstage/core-components';
import { UserMenu } from './UserMenu';
import { brandColors } from '../../theme/customization';

interface PageMeta { title: string; subtitle: string; }

const ROUTE_TITLES: Array<{ prefix: string; exact?: boolean } & PageMeta> = [
  { prefix: '/tech-radar',         title: 'Tech Landscape', subtitle: 'Technology assessments and recommendations' },
  { prefix: '/docs',               title: 'Documentation',        subtitle: 'Technical Documentation' },
  { prefix: '/documentation-menu', title: 'Documentation',        subtitle: 'Technical documentation and guides' },
  { prefix: '/our-tools-menu',     title: 'Hydra Tools',          subtitle: 'Internal tools and platforms' },
  { prefix: '/okd-consoles',       title: 'OKD Consoles',         subtitle: 'Navigate to your OpenShift clusters' },
  { prefix: '/marketplace',        title: 'Marketplace',          subtitle: 'Additional tools and resources' },
  { prefix: '/search',             title: 'Search',               subtitle: 'Search across the entire ecosystem' },
  { prefix: '/settings',           title: 'Settings',             subtitle: 'Manage your account and preferences' },
  { prefix: '/notifications',      title: 'Notifications',        subtitle: 'Stay updated with your platform activity' },
  { prefix: '/operators',          title: 'Hydra Operators',      subtitle: 'Browse and learn about all available Hydra platform operators' },
  { prefix: '/catalog', exact: true, title: 'Software Catalog',   subtitle: 'Browse our software ecosystem' },
  { prefix: '/subscriptions',       title: 'Subscriptions',        subtitle: 'Your connected platform services' },
  { prefix: '/harness',            title: 'Harness CI/CD',        subtitle: 'Pipeline executions and build history' },
  { prefix: '/quay',               title: 'Quay Images',          subtitle: 'Container image registry' },
  { prefix: '/datadog',            title: 'Datadog',              subtitle: 'Monitoring and observability' },
  { prefix: '/apim/applications',  title: 'APIM Application Lookup', subtitle: 'Resolve a Gravitee application id or OAuth client id to its name and owner' },
  { prefix: '/ideas',              title: 'Ideas Board',          subtitle: 'Submit and track Dev Portal feature ideas' },
  { prefix: '/impact-analysis', title: 'Impact Analysis', subtitle: 'See which systems and customers are affected when a system goes down' },
  { prefix: '/topology-viewer/builder', title: 'Topology Editor', subtitle: 'Build and edit your team\'s topology.json' },
  { prefix: '/topology-viewer',   title: 'Topology Viewer',      subtitle: 'Visualize system architecture from topology.json' },
  { prefix: '/ges-builder',        title: 'GES Builder',          subtitle: 'Build the features, SKUs, customers and associations to onboard into GES' },
  { prefix: '/gravitee-api-operator-builder', title: 'GraviteeAPI Operator Builder', subtitle: 'Build a GraviteeAPI custom resource YAML for the Hydra APIM operator' },
  { prefix: '/catalog-graph',      title: 'Catalog Graph',         subtitle: 'Visualize entity relationships' },
];

const DEFAULT_META: PageMeta = { title: 'Verint Developer Portal', subtitle: 'Welcome to the Developer Portal' };

const HIDE_PREFIXES = [
  '/infrastructure-graph', '/ai-marketplace',
  '/announcements', '/catalog-import',
];

const MINIMAL_HEADER_PREFIXES = ['/settings', '/search', '/notifications'];

function resolvePageMeta(pathname: string): PageMeta {
  for (const entry of ROUTE_TITLES) {
    if (entry.exact ? pathname === entry.prefix : pathname.startsWith(entry.prefix)) {
      return { title: entry.title, subtitle: entry.subtitle };
    }
  }
  return DEFAULT_META;
}

export const PortalHeader = () => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const location = useLocation();

  const appVersion = configApi.getOptionalString('app.version') ?? '';

  const { value: identity } = useAsync(async () => {
    try {
      return await identityApi.getBackstageIdentity();
    } catch {
      return undefined;
    }
  });

  const displayName = identity?.userEntityRef?.split('/').pop() || 'Guest';

  const isEntityPage = /^\/catalog\/[^/]+\/[^/]+\/[^/]+/.test(location.pathname);
  const hideHeader = isEntityPage || HIDE_PREFIXES.some(p => location.pathname.startsWith(p));
  if (hideHeader) return null;

  const { title, subtitle } = resolvePageMeta(location.pathname);
  const isMinimal = MINIMAL_HEADER_PREFIXES.some(p => location.pathname.startsWith(p));

  return (
    <BackstageHeader title={title} subtitle={subtitle} style={{
      background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primaryDark} 60%, #1a1a2e 100%)`,
      boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>
      {!isMinimal && <HeaderLabel label="Version" value={appVersion} />}
      {!isMinimal && <UserMenu displayName={displayName} />}
    </BackstageHeader>
  );
};
